/**
 * QuotaResetDialog Component
 *
 * Confirmation dialog for resetting quota usage counters.
 * Shows quota details and requires explicit confirmation.
 */

import React from 'react';
import { StatusBadge } from '../shared/StatusBadge';

export interface QuotaResetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  resetItem: {
    provider_name: string;
    pool_index: number;
    model_use?: string;
    quota_type?: string;
  } | null;
}

const quotaTypeLabels: Record<string, string> = {
  concurrency: '并发数',
  token_per_minute: 'Token/分钟',
  token_per_hour: 'Token/小时',
  token_per_day: 'Token/天',
  token_per_month: 'Token/月',
  token_total: 'Token总量',
  request_per_minute: '请求/分钟',
  request_per_hour: '请求/小时',
  request_per_day: '请求/天',
  request_per_month: '请求/月',
  request_total: '请求总量',
};

export const QuotaResetDialog: React.FC<QuotaResetDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  resetItem,
}) => {
  if (!isOpen || !resetItem) return null;

  const isSingleQuotaType = resetItem.quota_type !== undefined;
  const isPoolReset = resetItem.model_use === undefined || resetItem.model_use === '';
  const isAllReset = !isSingleQuotaType && !isPoolReset;

  const title = isSingleQuotaType
    ? '重置单个配额类型'
    : isPoolReset
    ? '重置池所有配额'
    : '重置模型配额';

  const description = isSingleQuotaType
    ? `即将重置 ${quotaTypeLabels[resetItem.quota_type!] || resetItem.quota_type} 配额。此操作将清除该配额类型的所有使用计数器。`
    : isPoolReset
    ? `即将重置 Pool #${resetItem.pool_index} 的所有配额。此操作将清除该池中所有模型的所有配额使用计数器。`
    : `即将重置模型 ${resetItem.model_use} 的所有配额。此操作将清除该模型的所有配额使用计数器。`;

  const warningMessage = isAllReset
    ? '这将影响模型的所有配额类型，包括并发数、Token 配额和请求配额。'
    : isPoolReset
    ? '这将影响池中所有模型的所有配额类型。'
    : '请确认您要执行此操作。';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-sm transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374a3 3 0 00-.542-.542l-6.263-3.732a3 3 0 00-3.358 0l-6.263 3.732c-.17.11-.34.264-.542.542zM12 12.75h.007v.008H12v-.008z"
                  />
                </svg>
              </div>

              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  {title}
                </h3>

                <div className="mt-3 space-y-3">
                  <p className="text-sm text-gray-500">
                    {description}
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">提供商</span>
                      <span className="font-medium text-gray-800">
                        {resetItem.provider_name}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">池索引</span>
                      <span className="font-medium text-gray-800">
                        #{resetItem.pool_index}
                      </span>
                    </div>

                    {!isPoolReset && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">模型用途</span>
                        <span className="font-medium text-gray-800">
                          {resetItem.model_use}
                        </span>
                      </div>
                    )}

                    {isSingleQuotaType && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">配额类型</span>
                        <StatusBadge variant="warning" size="sm">
                          {quotaTypeLabels[resetItem.quota_type!] || resetItem.quota_type}
                        </StatusBadge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <svg
                      className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                    <div className="text-sm text-yellow-800">
                      <span className="font-medium">警告：</span>
                      {warningMessage}
                      此操作无法撤销。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
            >
              确认重置
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaResetDialog;